import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService, VestaUserType } from './auth.service';
import { OrganizationService } from './organization.service';
import * as Parse from 'parse';

interface IPublicPropertiesPayload {
    pageSize: number;
    pageNum: number;
    city: string;
    state: string;
    type: string;
    organizationId: string;
}

@Injectable()
export class PropertyService {
    #streetViewAPIKey: string = environment.googleApiKey;
    #showPropertyMenu: boolean = false;

    get showPropertyMenu() {
        return this.#showPropertyMenu;
    }

    set showPropertyMenu(value: boolean) {
        this.#showPropertyMenu = value;
    }

    constructor(
        private sanitizer: DomSanitizer,
        private auth: AuthService,
        private orgService: OrganizationService
    ) {}

    /**
     * Returns the google street view image url for the property.
     */
    getStreetViewUrl(address: string, city: string, zip: number, state: string, width: number, height: number) {
        return `https://maps.googleapis.com/maps/api/streetview?` +
            `location=${encodeURIComponent(address)},${city},${state} ${zip}` +
            `&key=${this.#streetViewAPIKey}&size=${width}x${height}`;

    }

    getPublicProperties({ pageSize, pageNum, city, state, type, organizationId }: IPublicPropertiesPayload): Promise<Parse.Object[]> {
        return Parse.Cloud.run('getPublicProperties', {
            pageSize,
            pageNum,
            city,
            state,
            type,
            organizationId
        });
    }

    getPublicProperty(propertyId: string, organizationId: string) {
        return Parse.Cloud.run('getPublicProperties', {
            id: propertyId,
            organizationId: organizationId
        });
    }

    /**
     * Returns the sanitized google street view background image style.
     * @param property The property object
     * @param width
     * @param height
     */
    getCoverImageAsBackground(property: Parse.Object, width: number, height: number) {
        let url = this.getStreetViewUrl(
            property.get('address'),
            property.get('city'),
            property.get('zip'),
            property.get('state'),
            width,
            height);

        if (property.has('coverPhoto')) {
            if (property.get('coverPhoto').has('thumbnail')) {
                url = property.get('coverPhoto').get('thumbnail').url();
            } else {
                url = property.get('coverPhoto').has('file') ? property.get('coverPhoto').get('file').url() : '';
            }
        }

        return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
    }

    assembleMainQuery(userType: VestaUserType) {
        let query: Parse.Query = new Parse.Query('Property');
        query.equalTo('organization', this.orgService.organization);

        if (userType === 'PM') {
            query.equalTo('projectManager', Parse.User.current());
        } else if (userType === 'Vendor') {
            query.contains('vendors', Parse.User.current()?.id as string);
        }

        return query;
    }

    fetchProperties(query: Parse.Query, pageSize: number, pageIndex: number) {
        // Apply defaults
        query.limit(pageSize);
        query.skip(pageSize * pageIndex);
        query.include('owner');
        query.include('estatedInfo');
        query.include('offerFlip');
        query.include('offerRental');
        query.include('aaFlip');
        query.include('aaRental');
        query.include('saFlip');
        query.include('saRental');
        query.include('coverPhoto');
        query.include('rehabFlip');
        query.include('rehabRental');
        query.include('responsibleForSale');
        query.include('salesComps2');
        query.include('listingDocument2');
        query.include('acquisionContract2');
        query.include('resaleContract2');
        query.descending('createdAt');

        query.withCount();

        return query.find();
    }

    // Returns a Parse Query to filter by the address search input.
    getAddressFilter(searchTerm: string, useComponentMethod: boolean = false): Parse.Query | boolean {
        if (searchTerm === '') {
            return false;
        }

        const keyword = searchTerm.toLowerCase().trim();

        let addressQuery: Parse.Query = new Parse.Query('Property');
        addressQuery.equalTo('organization', this.orgService.organization);
        addressQuery.contains('address', keyword);

        if (useComponentMethod) {
            // This method is used as a fallback if the direct search did not produce results.
            const addressParts = keyword.split(' ');
            if (addressParts.length > 1) {
                // Search for combinations of address components
                addressParts.forEach(element => {
                    const qPart = new Parse.Query('Property');
                    qPart.contains('address', element);
                    addressQuery = Parse.Query.or(addressQuery, qPart);
                });
            }
        }

        const cityQuery: Parse.Query = new Parse.Query('Property');
        addressQuery.equalTo('organization', this.orgService.organization);
        cityQuery.contains('city', keyword);

        const zipQuery: Parse.Query = new Parse.Query('Property');
        addressQuery.equalTo('organization', this.orgService.organization);
        zipQuery.contains('zip', keyword);

        return Parse.Query.or(
            addressQuery,
            cityQuery,
            zipQuery
        );
    }

    getStatusFilter(filters: any, viewSetting: '' | 'sales' | 'acquisition' | 'tc', userType: string): Parse.Query | boolean {
        if (viewSetting === '') {
            if (filters.status === 'all') {
                // If SA or TC uses All it will not include any imported or offered properties
                if (userType === 'SA' || userType === 'TC') {
                    const q1: Parse.Query = new Parse.Query('Property').notEqualTo('status', 'imported').equalTo('organization', this.orgService.organization);
                    const q2: Parse.Query = new Parse.Query('Property').notEqualTo('status', 'offered').equalTo('organization', this.orgService.organization);

                    return Parse.Query.and(q1, q2);
                }

                return false;
            }

            return new Parse.Query('Property').equalTo('status', filters.status).equalTo('organization', this.orgService.organization);
        } else if (viewSetting === 'sales') {
            const q1: Parse.Query = new Parse.Query('Property').equalTo('status', 'accepted').equalTo('organization', this.orgService.organization);
            const q2: Parse.Query = new Parse.Query('Property').equalTo('status', 'owned').equalTo('organization', this.orgService.organization);

            return Parse.Query.or(q1, q2);
        } else if (viewSetting === 'acquisition') {
            const q1: Parse.Query = new Parse.Query('Property').equalTo('status', 'imported').equalTo('organization', this.orgService.organization);
            const q2: Parse.Query = new Parse.Query('Property').equalTo('status', 'offered').equalTo('organization', this.orgService.organization);

            return Parse.Query.or(q1, q2);
        } else if (viewSetting === 'tc') {
            // As a TC, when first loading or navigating back to the Properties List, the page filter will reset to only properties of the Pending state
            return new Parse.Query('Property').equalTo('status', 'pending').equalTo('organization', this.orgService.organization);
        }

        return false;
    }

    getUserFilter(filters: any): Parse.Query | boolean {
        if (filters.user === 'all') {
            return false;
        } else if (filters.user === 'current') {
            // filters to only properties where this user is either the owner or responsibleForSale user
            const q1: Parse.Query = new Parse.Query('Property').equalTo('owner', this.auth.currentUser).equalTo('organization', this.orgService.organization);
            const q2: Parse.Query = new Parse.Query('Property').equalTo('responsibleForSale', this.auth.currentUser).equalTo('organization', this.orgService.organization);

            return Parse.Query.or(q1, q2);
        } else {
            // show properties where the selected user is either the owner or responsibleForSale
            const user = new Parse.User();
            user.id = filters.user;

            const q1: Parse.Query = new Parse.Query('Property').equalTo('owner', user).equalTo('organization', this.orgService.organization);
            const q2: Parse.Query = new Parse.Query('Property').equalTo('responsibleForSale', user).equalTo('organization', this.orgService.organization);

            return Parse.Query.or(q1, q2);
        }
    }

    fetchPropertyById(id: string) {
        const property = new Parse.Object('Property');
        property.id = id;

        return property.fetchWithInclude([
            'owner',
            'estatedInfo',
            'offerFlip',
            'offerRental',
            'aaFlip',
            'aaRental',
            'saFlip',
            'saRental',
            'coverPhoto',
            'rehabFlip',
            'rehabRental',
            'responsibleForSale',
            'salesComps2',
            'listingDocument2',
            'acquisionContract2',
            'resaleContract2'
        ]);
    }

    getPropertyPhotos(property: Parse.Object) {
        const query: Parse.Query = new Parse.Query('Photo');

        query.equalTo('organization', this.orgService.organization);
        query.equalTo('property', property);
        query.descending('createdAt');

        return query.find();
    }

    async setStreetViewAsCoverPhoto(property: Parse.Object, streetViewImage: string) {
        const url = streetViewImage;
        const file = new Parse.File('streetview.png', { uri: url as string });
        const photo = new Parse.Object('Photo');

        photo.set('name', 'streetview.png');
        photo.set('organization', this.orgService.organization);
        photo.set('file', file);

        await photo.save();

        property.set('coverPhoto', photo);
    }

    getPublicMedia(property: Parse.Object) {
        return Parse.Cloud.run('getPublicMedia', { propertyId: property.id });
    }

    lockAddress(property: Parse.Object) {
        // locking the address after the first time viewing the property
        // first time is the only chance to change the property address that import might have missed
        if (!property.has('addressLocked') || property.get('addressLocked') === false) {
            property.set('addressLocked', true);

            return property.save();
        }

        return Promise.resolve();
    }

    loadUsers() {
        const adminQuery: Parse.Query = new Parse.Query(Parse.User).equalTo('type', 'Admin').equalTo('organization', this.orgService.organization);
        const pmQuery: Parse.Query = new Parse.Query(Parse.User).equalTo('type', 'PM').equalTo('organization', this.orgService.organization);

        return Parse.Query.or(adminQuery, pmQuery).find();
    }

    fetchPropertyHistory(property: Parse.Object, pageIndex: number, pageSize: number) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('PropertyHistory');

        query.equalTo('organization', this.orgService.organization);
        query.equalTo('property', property);
        query.descending('createdAt');
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);

        query.withCount();

        return query.find();
    }

    setPendingState(propertyId: string, buyerEmail: string) {
        return Parse.Cloud.run('setPendingState', { propertyId: propertyId, buyerEmail: buyerEmail });
    }
}
